/**
 * @file   src\services\apiService.js
 * @brief  This file is responsible for api service
 * @date   April, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import axios from "axios";
import jwtDecode from "jwt-decode";
import _ from "lodash";
import {
  clearLocalStorageOnLogout,
  getLocalStorage,
} from "../../helpers/common";
import { USER_INFO_KEY } from "../../constants/common";
import { tokenRefresh } from "../v2/loginService";
import { GET_GOOGLE_USER_INFO } from "../../constants/endPoints";
import { getTenantSubdomain } from "../../helpers/tenant";
import { getAppPathname } from "@/helpers/basePath";

const clearSession = () => {
  clearLocalStorageOnLogout();
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    ) ||
    getAppPathname() === "/home"
  ) {
    window.location.reload();
  }
};

// Reads the active BLS tenant slug from the current URL's subdomain and
// returns an `x-bls-env` header pair for the backend's tenant middleware.
// Frontend and backend are typically on different hosts in production
// (e.g. lar.<domain> → api.<domain>), so hostname-based detection on the
// API side doesn't work — this header is the load-bearing path for tenant
// resolution on every request, public or authenticated.
// Returns an empty object when no tenant slug is detectable so Gitcha
// consumer behaviour stays unchanged.
export const getBlsEnvHeader = () => {
  const sub = getTenantSubdomain();
  if (!sub || ["www", "app", "localhost"].includes(sub)) return {};
  return { "x-bls-env": sub };
};

const getHeaders = async (accessToken, refToken, userInfo, header) => {
  const decodedToken = jwtDecode(accessToken);
  const expirationTime = decodedToken?.exp * 1000 - 60000;

  if (Date.now() < expirationTime) {
    if (header) {
      return { header };
    } else {
      return { Authorization: accessToken };
    }
  } else {
    const decodedRefreshToken = jwtDecode(refToken);
    if (Date.now() < decodedRefreshToken?.exp * 1000) {
      return await tokenRefresh({ refreshToken: refToken })
        .then((res) => {
          if (res?.result?.accessToken) {
            Object.entries(userInfo?.token).forEach(([key, value]) => {
              if (key === "accessToken") {
                userInfo.token["accessToken"] = res.result.accessToken;
              }
            });
            localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
            return { Authorization: res.result.accessToken };
          } else {
            // alert("Your session has expired. Please login to continue.");
            clearSession();
          }
        })
        .catch((error) => {
          // alert("Your session has expired. Please login to continue.");
          clearSession();
        });
    } else {
      // alert("Your session has expired. Please login to continue.");
      clearSession();
    }
  }
};

// Shared by apiCall and apiCallBlob: resolves the bearer token from a
// passed-in token or localStorage, refreshing it via getHeaders() if it's
// expired. Kept in one place so the two callers can't drift on the
// auth/refresh contract.
export const resolveAuthHeaders = async (token, header) => {
  let accessToken = null;
  let refreshToken = null;
  let userInfo = null;
  if (token) {
    accessToken = token;
  } else {
    userInfo = getLocalStorage(USER_INFO_KEY);
    if (userInfo && userInfo !== "") {
      accessToken = _.get(userInfo, "token.accessToken", null);
      refreshToken = _.get(userInfo, "token.refreshToken", null);
    }
  }

  let headers = { Authorization: "Bearer " + accessToken };
  if (userInfo != null && refreshToken) {
    headers = await getHeaders(accessToken, refreshToken, userInfo, header);
  }
  return headers;
};

export async function apiCall(
  url,
  method,
  data,
  userId = null,
  token = null,
  header = null,
  isFileUploadToS3 = false,
  signal = null,
) {
  let headers = await resolveAuthHeaders(token, header);

  if (url != GET_GOOGLE_USER_INFO) {
    headers = {
      ...headers,
      "api-key": process.env.REACT_APP_REST_API_KEY,
      ...getBlsEnvHeader(),
      // "ngrok-skip-browser-warning": true,
    };
  }

  const response = await axios({
    url,
    method,
    params: method === "GET" && data ? data : {},
    data,
    headers,
    signal,
  });

  if (response) {
    if (isFileUploadToS3) {
      // For S3 file upload response is different than internal API response. Data is empty in this case, so return response itself instead of response data.
      return response;
    } else {
      return response.data;
    }
  } else {
    return [];
  }
}

export async function apiCallWithoutToken(
  url,
  method,
  data,
  userId = null,
  token = null,
  endPoint,
  signal = null,
) {
  const headers = {
    "Content-Type": "application/json",
    "api-key": process.env.REACT_APP_REST_API_KEY,
    ...getBlsEnvHeader(),
  };

  const response = await axios({
    url,
    method,
    params: method === "GET" && data ? data : {},
    data,
    headers,
    signal,
  });
  if (response) {
    return response.data;
  } else {
    return [];
  }
}

// Same auth/JWT-refresh contract as apiCall, but requests a binary
// responseType and returns the raw axios response (not response.data) so
// callers can read both the Blob body and response headers (e.g.
// Content-Disposition for the server-supplied filename).
export async function apiCallBlob(url, method = "GET", timeout = 0) {
  let headers = await resolveAuthHeaders(null, null);

  headers = {
    ...headers,
    "api-key": process.env.REACT_APP_REST_API_KEY,
    ...getBlsEnvHeader(),
  };

  return await axios({
    url,
    method,
    headers,
    responseType: "blob",
    timeout,
  });
}

